import type { components } from '@feedfm/clips-services-api';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import _collectionArtworkMap from '../../data/collection-artwork-map.json';
import { LazyThumbnail } from '../LazyThumbnail';
import * as styles from './CollectionArtwork.styles';

/**
 * The type of the above JSON file. This is a temporary implementation until we have a proper
 * backend.
 */
type ClipSelection = [
  topLeft: number,
  topRight: number,
  bottomLeft: number,
  bottomRight: number,
];
type CollectionArtworkMapJson = Record<string, undefined | ClipSelection>;

const collectionArtworkMap =
  _collectionArtworkMap as unknown as CollectionArtworkMapJson;

const FALLBACK_COVER = '/album-cover.svg';

export interface CollectionArtworkProps {
  /** Four pre-signed artwork URLs representing the collection. Used as a fallback when
   *  `thumbnailUrl` is not provided. */
  artworkPreSignedUrls: components['schemas']['ArtworkPreSignedUrls'];
  /** Single signed CloudFront URL for the server-generated composite thumbnail. When present,
   *  it's rendered instead of the 2x2 grid — one HTTP request, much smaller payload. */
  thumbnailUrl?: string | null;
  /** Custom click handler for the artwork grid */
  onClick?: () => void;
}

type ClipRow = components['schemas']['Clip'];

function getNextAvailableClip(
  clips: ClipRow[],
  preferredClipIds: number[],
  selectedClips: ClipRow[],
): ClipRow {
  if (clips.length === 0) {
    throw new Error('getNextAvailableClip cannot be called with no clips');
  }

  const usedClipIds = new Set<number>([
    ...preferredClipIds,
    ...selectedClips.map((c) => c.clipId),
  ]);

  // As the edgest of edge cases, we will re-use clips when there aren't at least 4.
  return clips.find((c) => !usedClipIds.has(c.clipId)) || clips[0];
}

export const CollectionArtwork = ({
  artworkPreSignedUrls: artwork,
  thumbnailUrl,
  onClick,
}: CollectionArtworkProps) => {
  // Get the four artwork URLs for this collection (fallback path)
  const artworkUrls = useMemo(
    () => [
      artwork[0] ?? FALLBACK_COVER,
      artwork[1] ?? FALLBACK_COVER,
      artwork[2] ?? FALLBACK_COVER,
      artwork[3] ?? FALLBACK_COVER,
    ],
    [artwork],
  );

  if (thumbnailUrl) {
    return (
      <LazyThumbnail
        src={thumbnailUrl}
        alt=""
        sx={{ ...styles.container, borderRadius: '4px' }}
        onClick={onClick}
      />
    );
  }

  return (
    <Box sx={styles.container} onClick={onClick}>
      <Box sx={styles.grid}>
        {artworkUrls.map((url, index) => (
          <Box
            key={index}
            sx={{
              ...styles.gridItem,
              backgroundImage: `url(${url})`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
